import React, { CSSProperties, memo, PropsWithChildren, useMemo, useState } from 'react';
import { R } from '@core/utils/r';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { CommonService } from '@business/services';
import { tap } from 'rxjs/operators';

export interface AvatarProps {
  size?: number;
  round?: boolean;
  image?: string | null | File;
  style?: React.CSSProperties;
  emptyIcon?: string;
  text?: string;
  borderRadius?: number;
  border?: boolean;
  onClick?(): void;
  isLoading?: boolean;
  onEdit?: (file: File) => void;
}

export const Avatar = memo((props: PropsWithChildren<AvatarProps>) => {
  const { text, emptyIcon, image, size = 50, border = false, onClick, isLoading = false, round = true } = props;
  const borderRadius = useMemo(() => {
    if (round) return Math.ceil(size / 2);
    return R.isNumber(props.borderRadius) ? props.borderRadius : 10;
  }, [props.borderRadius, round, size]);

  const borderWidth = R.isNumber(border) ? border : !border ? 0 : 1;
  const textSize = Math.round(size * 0.35);

  const containerStyle: CSSProperties = useMemo(() => {
    return {
      width: size,
      height: size,
      borderWidth,
      borderRadius,
      padding: undefined,
      backgroundColor: '#e3e3e3',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: onClick ? 'pointer' : undefined,
      position: 'relative',
      overflow: 'hidden',
      ...props.style,
    };
  }, [borderRadius, borderWidth, onClick, props.style, size]);

  const [isHover, setHover] = useState(false);
  const symbol = useMemo(() => {
    if (!text) return '';
    return text
      .split(/\s+/)
      .map(part => part && part[0].toUpperCase())
      .filter(part => part)
      .splice(0, 2)
      .join('');
  }, [text]);

  const icon = useMemo(() => {
    return <i className={emptyIcon} style={{ fontSize: textSize }} />;
  }, [emptyIcon, textSize]);

  const showIcon = useMemo(() => !!icon && !symbol && !image, [icon, image, symbol]);
  const showSymbol = useMemo(() => !!symbol && !image, [image, symbol]);
  const showImage = useMemo(() => !!image, [image]);

  const textStyles = useMemo(() => (showSymbol ? { fontSize: textSize } : {}), [showSymbol, textSize]);
  const imageStyles: CSSProperties = useMemo(() => ({ width: size, height: size, objectFit: 'cover', borderRadius }), [borderRadius, size]);
  const srcAttr = useMemo(() => {
    if (R.isString(image)) return image;
    if (R.isNil(image)) return undefined;
    return URL.createObjectURL(image);
  }, [image]);

  return (
    <div style={containerStyle} onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {showImage && <img style={{ ...imageStyles }} src={srcAttr}></img>}
      {showSymbol && <span style={{ fontWeight: 'bold', ...textStyles }}>{symbol}</span>}
      {showIcon && <span style={{ ...textStyles }}>{icon}</span>}
      {isLoading && (
        <div
          style={{ backgroundColor: '#00000050', width: size, height: size, borderRadius, position: 'absolute' }}
          className="d-flex justify-content-center align-items-center"
        >
          <LoadingIndicator size={10} dotStyle={{ backgroundColor: '#FFF' }} />
        </div>
      )}
      {isHover && !!props.onEdit && (
        <a
          onClick={() =>
            CommonService.chooseImage()
              .pipe(tap(file => props.onEdit!(file[0])))
              .subscribe()
          }
          style={{
            cursor: 'pointer',
            position: 'absolute',
            overflow: 'hidden',
            backgroundColor: '#00000050',
            padding: 20,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="bx bxs-cloud-upload" style={{ fontSize: size / 3, color: '#FFF' }}></i>
        </a>
      )}
      {props.children}
    </div>
  );
});
